import { useEffect, useState } from 'react';

import { ShiftRequestsService } from '../../api/ShiftRequestsAPI';
import s from '../../styles/dashboard.module.css';

// One drawer, three jobs, because they are the same conversation seen from
// either end:
//
//   newRequestKind  a technician asks for time off, a day, or different hours
//   newFixFor       a technician asks for a clock time to be corrected
//   request         somebody reads a request — and an admin decides it
//
// Approving is the only path that writes to a timecard, and the button only
// exists for an admin looking at something still pending. Everyone else is
// reading.
const fmtTime = (v) => (v
  ? new Date(v).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  : '—');

const fmtDay = (v) => (v
  ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  : '—');

// A datetime-local value for an instant, in the browser's own zone — the one
// the person reading the clock is standing in.
function toLocalInput(v) {
  if (!v) return '';
  const d = new Date(v);
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 16);
}

const today = () => new Date().toISOString().slice(0, 10);

// The four reasons a clock time is nearly always wrong. Offered as chips
// because typing them out on a tablet is the slowest part of asking.
const FIX_REASONS = [
  'Forgot to clock out', 'Forgot to clock in', 'Wrong time entered', 'Approved by phone',
];

function Row({ k, v }) {
  return (
    <div className={s.drawerRow}>
      <span className={s.drawerRowKey}>{k}</span>
      <span className={s.drawerRowVal}>{v}</span>
    </div>
  );
}

export function RequestDecisionModal({
  state, idStaff, isAdmin, canAct, busy, defaultDay, onDecide, onSubmitted, onClose,
}) {
  const request = state?.request || null;
  const fixFor = state?.newFixFor || null;
  const kind = state?.newRequestKind || null;

  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [fromDate, setFromDate] = useState(defaultDay || today());
  const [toDate, setToDate] = useState(defaultDay || today());
  const [details, setDetails] = useState('');
  const [clockIn, setClockIn] = useState(() => toLocalInput(fixFor?.clockInAt));
  const [clockOut, setClockOut] = useState(() => toLocalInput(fixFor?.clockOutAt));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      if (fixFor) {
        // Send a time only where it was actually changed. An unchanged
        // clock-out left in the payload would be a correction nobody asked
        // for, and an empty one reads as "reopen this shift".
        const changed = {
          reason,
          proposedClockInAt: clockIn && clockIn !== toLocalInput(fixFor.clockInAt)
            ? new Date(clockIn).toISOString() : undefined,
          proposedClockOutAt: clockOut && clockOut !== toLocalInput(fixFor.clockOutAt)
            ? new Date(clockOut).toISOString() : undefined,
        };
        if (!changed.proposedClockInAt && !changed.proposedClockOutAt) {
          setError('Change a time first.');
          setSaving(false);
          return;
        }
        await ShiftRequestsService.fixClockTime(idStaff, fixFor.idShift, changed);
      } else if (kind === 'time_off') {
        await ShiftRequestsService.requestTimeOff(idStaff, fromDate, toDate, reason);
      } else if (kind === 'day_unavailable') {
        await ShiftRequestsService.markDayUnavailable(idStaff, fromDate, reason);
      } else if (kind === 'hours_change') {
        await ShiftRequestsService.changeHours(idStaff, details, reason);
      }
      onSubmitted();
    } catch (err) {
      setError(err?.error || err?.message || 'That request did not go through.');
    } finally {
      setSaving(false);
    }
  };

  const withdraw = async () => {
    setSaving(true);
    setError('');
    try {
      await ShiftRequestsService.withdraw(request.idRequest, idStaff);
      onSubmitted();
    } catch (err) {
      setError(err?.error || err?.message || 'Could not withdraw that.');
    } finally {
      setSaving(false);
    }
  };

  const nothingToFix = !!state?.noShiftToFix;
  const isPending = request?.status === 'pending';

  const heading = nothingToFix ? 'Nothing to fix yet'
    : request ? request.kindLabel
    : fixFor ? 'Fix a clock time'
    : state?.label || 'New request';

  const sub = nothingToFix
    ? 'A clock time can only be corrected once there is one'
    : request
    ? `${request.staffName || 'Someone'} · asked ${fmtDay(request.createAt)}`
    : fixFor
      ? `${fmtDay(fixFor.clockInAt)} · ${fmtTime(fixFor.clockInAt)} – ${fmtTime(fixFor.clockOutAt)}`
      : 'Goes to an admin to approve';

  // An admin can only decide something still pending; anything else is history.
  const canDecide = isAdmin && isPending;
  const canWithdraw = !isAdmin && canAct && isPending;

  return (
    <>
      <div className={s.drawerScrim} onClick={saving || busy ? undefined : onClose} />
      <aside className={s.drawer} role="dialog" aria-label={heading}>
        <div className={s.drawerHead}>
          <div>
            <div className={s.drawerEyebrow}>{request ? 'Request' : 'Ask for'}</div>
            <div className={s.drawerTitle}>{heading}</div>
            <div className={s.drawerSub}>{sub}</div>
          </div>
          <button className={s.drawerClose} onClick={onClose} disabled={saving || busy} aria-label="Close">×</button>
        </div>

        <div className={s.drawerBody}>
          {request && (
            <div className={s.drawerCard}>
              <div className={s.drawerCardHead}>
                <span className={s.drawerCardLabel}>What was asked</span>
                <span className={s.drawerCardValue}>{request.status}</span>
              </div>
              {request.kind === 'clock_fix' ? (
                <>
                  <Row k="New clock-in" v={request.proposedClockInAt ? fmtTime(request.proposedClockInAt) : 'unchanged'} />
                  <Row k="New clock-out" v={request.proposedClockOutAt ? fmtTime(request.proposedClockOutAt) : 'unchanged'} />
                </>
              ) : request.fromDate ? (
                <>
                  <Row k="From" v={fmtDay(request.fromDate)} />
                  <Row k="To" v={fmtDay(request.toDate)} />
                </>
              ) : (
                <Row k="Proposed" v={request.details || '—'} />
              )}
              <Row k="Reason" v={request.reason || '—'} />
              {request.decidedByName && <Row k="Decided by" v={request.decidedByName} />}
              {request.decisionNote && <Row k="Note" v={request.decisionNote} />}
            </div>
          )}

          {fixFor && (
            <div className={`${s.drawerCard} ${s.drawerCardSunken}`}>
              <div className={s.payFieldRow}>
                <label className={s.payField}>
                  <span className={s.drawerCardLabel}>Clock in</span>
                  <input className={s.payInput} type="datetime-local" value={clockIn}
                         disabled={saving} onChange={(e) => setClockIn(e.target.value)} />
                </label>
                <label className={s.payField}>
                  <span className={s.drawerCardLabel}>Clock out</span>
                  <input className={s.payInput} type="datetime-local" value={clockOut}
                         disabled={saving} onChange={(e) => setClockOut(e.target.value)} />
                </label>
              </div>
              <div className={s.reasonChips}>
                {FIX_REASONS.map((r) => (
                  <button
                    key={r}
                    type="button"
                    disabled={saving}
                    onClick={() => setReason(reason === r ? '' : r)}
                    className={`${s.reasonChip} ${reason === r ? s.reasonChipOn : ''}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
              <div className={s.drawerNote}>
                An admin approves this before anything changes. The original
                timecard is kept either way.
              </div>
            </div>
          )}

          {(kind === 'time_off' || kind === 'day_unavailable') && (
            <div className={`${s.drawerCard} ${s.drawerCardSunken}`}>
              <div className={s.payFieldRow}>
                <label className={s.payField}>
                  <span className={s.drawerCardLabel}>{kind === 'time_off' ? 'First day' : 'Day'}</span>
                  <input className={s.payInput} type="date" value={fromDate}
                         disabled={saving} onChange={(e) => setFromDate(e.target.value)} />
                </label>
                {kind === 'time_off' && (
                  <label className={s.payField}>
                    <span className={s.drawerCardLabel}>Last day</span>
                    <input className={s.payInput} type="date" value={toDate}
                           disabled={saving} onChange={(e) => setToDate(e.target.value)} />
                  </label>
                )}
              </div>
            </div>
          )}

          {kind === 'hours_change' && (
            <div className={`${s.drawerCard} ${s.drawerCardSunken}`}>
              <span className={s.drawerCardLabel}>The hours you want</span>
              <textarea className={s.payTextarea} rows={3} value={details} disabled={saving}
                        placeholder="Mon–Thu 8–4, no Fridays"
                        onChange={(e) => setDetails(e.target.value)} />
            </div>
          )}

          {nothingToFix && (
            <div className={s.drawerCard}>
              <div className={s.drawerCardLabel}>Nothing to correct</div>
              <div className={s.drawerNote}>
                There is no clock entry on this day to fix. Clock in and out
                first, then come back and correct the times.
              </div>
            </div>
          )}

          {!request && !nothingToFix && (
            <div className={s.drawerCard}>
              <span className={s.drawerCardLabel}>Why</span>
              <textarea className={s.payTextarea} rows={2} value={reason} disabled={saving}
                        placeholder="Optional, but it helps whoever decides"
                        onChange={(e) => setReason(e.target.value)} />
            </div>
          )}

          <div className={s.drawerCard}>
            <div className={s.drawerCardLabel}>What happens next</div>
            <div className={s.drawerNote}>
              {canDecide
                ? request.kind === 'clock_fix'
                  ? 'Approving rewrites the timecard and keeps the original on record. Declining leaves it as it is.'
                  : 'Approving puts this on record. It does not yet stop anyone being booked on those days.'
                : request
                  ? isPending
                    ? 'Waiting for an admin to approve or decline it.'
                    : request.status === 'withdrawn'
                      ? 'Withdrawn, so nothing changed.'
                      : `${request.status[0].toUpperCase()}${request.status.slice(1)}`
                        + (request.decidedByName ? ` by ${request.decidedByName}.` : '.')
                  : 'This goes to an admin. Nothing changes until they approve it.'}
            </div>

            {canDecide && (
              <textarea className={s.payTextarea} rows={2} value={note} disabled={busy}
                        placeholder="A note for whoever asked (optional)"
                        onChange={(e) => setNote(e.target.value)} />
            )}

            {(error) && <div className={s.nextStepError}>{error}</div>}

            <div className={s.drawerActions}>
              {canDecide && (
                <>
                  <button className={s.drawerPrimary} disabled={busy} onClick={() => onDecide(true, note)}>
                    {busy ? 'Saving…' : 'Approve'}
                  </button>
                  <button className={s.drawerSecondary} disabled={busy} onClick={() => onDecide(false, note)}>
                    Decline
                  </button>
                </>
              )}

              {!request && !nothingToFix && (
                <button className={s.drawerPrimary} disabled={saving} onClick={submit}>
                  {saving ? 'Sending…' : 'Send the request'}
                </button>
              )}

              {canWithdraw && (
                <button className={s.drawerSecondary} disabled={saving} onClick={withdraw}>
                  Withdraw
                </button>
              )}

              <button className={s.drawerSecondary} onClick={onClose} disabled={saving || busy}>
                Close
              </button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
